import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import UploadMultipleImages from '../UploadMultipleImages';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  LabelText,
  MoreImages,
  ResetButton,
} from '../VariationOne';
import {
  ImageGrid,
  ImagesBox,
  StyledTextArea,
  StyledUploadMultipleImages,
  StyledUploadedImages,
  TextAreaBox,
  TextPlaceholder,
  UploadedMultipleImages,
} from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import { ActionBox, ActionTitle } from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationStyles';

export interface ImageVariation {
  leftImageSrc: string;
  rightText: string;
  textPosition: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  quotes: boolean;
  brakes: boolean;
}

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

interface Props {
  inArticle?: boolean;
}

const VariationFullImageSubtitle: FC<Props> = ({ inArticle }) => {
  const [showContent, setShowContent] = useState(true);
  const [text, setText] = useState('');
  const [multipleVariations, setMultipleVariations] = useState(false);
  const [quotes, setQuotes] = useState(false);
  const [brakes, setBrakes] = useState(false);
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setText('');
    setBrakes(false);
    setQuotes(false);
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      textPosition: leftImage.textPosition,
      rightText: multipleVariations ? leftImage.text : text,
      quotes: !multipleVariations ? quotes : leftImage.quotes,
      brakes: !multipleVariations ? brakes : leftImage.brakes,
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const renderMultipleLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      hasText
      multipleVariations
    />
  ));

  const isResetDisabled = !leftImages?.length && !text && quotes && !brakes;

  return (
    <>
      <VariationsStyles />
      <Box>
        <Header>
          <div>
            <Heading $type="button">Full Image + Text</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one full image and text with the necessary parameters
            </Text>
          </div>
          <HeaderButtons>
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            {multipleVariations ? (
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">Full Images</LabelText>
                {!!leftImages?.length ? (
                  <>
                    <UploadedMultipleImages>
                      {renderMultipleLeftImages}
                    </UploadedMultipleImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={1}
                    noneDivider
                    quotes={false}
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
            ) : (
              <ImageGrid>
                <ImagesBox $hasImages={!!leftImages?.length}>
                  <LabelText $type="smallHeadline">Full Images</LabelText>
                  {!!leftImages?.length ? (
                    <>
                      <StyledUploadedImages>
                        {renderLeftImages}
                      </StyledUploadedImages>
                      <MoreImages>
                        <AttachedCountBadge count={leftImages.length} />
                        <AddMoreButton
                          onClick={() => setShowMoreImagesModal(true)}
                          color="colorized"
                        >
                          Add more images
                        </AddMoreButton>
                      </MoreImages>
                    </>
                  ) : (
                    <StyledUploadMultipleImages
                      coverIndex={2}
                      noneDivider
                      quotes={false}
                      setImages={setLeftImages}
                    />
                  )}
                </ImagesBox>
                <RightColumn>
                  <TextAreaBox>
                    <div>
                      <LabelText $type="smallHeadline">Full Text</LabelText>
                      <StyledTextArea
                        onChange={(e) => setText(e.target.value)}
                        value={text}
                        placeholder="Add text"
                      ></StyledTextArea>
                    </div>
                    <div>
                      <ActionTitle $type="smallHeadline">
                        Text Parameters
                      </ActionTitle>
                      <ActionBox>
                        <Checkbox
                          checked={quotes}
                          onChange={() => setQuotes((prev) => !prev)}
                        >
                          Quotes
                        </Checkbox>
                        <Checkbox
                          checked={brakes}
                          onChange={() => setBrakes((prev) => !prev)}
                        >
                          Custom brakes
                        </Checkbox>
                      </ActionBox>
                    </div>
                  </TextAreaBox>
                  {!!leftImages?.length && <TextPlaceholder />}
                </RightColumn>
              </ImageGrid>
            )}
          </>
        )}
      </Box>
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button disabled={!leftImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationFullImageSubtitle;
